/**
 * CreateDatasetModal — Carbon modal to define a new Dataset.
 *
 * The user picks a KB, optionally an ontology node as anchor, writes a
 * dataset name & description, then builds the column schema (key, label, type,
 * unit) with an Add/Remove row editor.
 *
 * Schema can be seeded from two sources (shown side-by-side):
 *   1. "Suggest schema from document" — pick a KB document, AI inspects its tables.
 *   2. "Use an ontology to create the schema" — pick an ontology + concept,
 *      AI derives measurable properties from the ontology graph.
 */

import { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  MultiSelect,
  Button,
  Tag,
  InlineLoading,
  Stack,
  InlineNotification,
} from '@carbon/react';
import { Add, TrashCan, MagicWand } from '@carbon/icons-react';
import type { KnowledgeBase, KBDocument } from '@snxt/snxt-onto-share';
import type { DatasetColumn } from '@snxt/snxt-onto-share';
import type { Ontology } from '@shared/types/ontology';
import { knowledgeBaseApi } from '@/lib/api/knowledge-base.api';
import { datasetApi } from '@/lib/api/dataset.api';
import { ontologyApi } from '@/lib/api/ontology.api';

// The API returns raw DB rows for nodes, which have both `id` (UUID) and
// `node_id` (semantic string like "coating_adhesion"). The backend queries
// on `node_id`, so we use that as the Select value.
interface RawOntologyNode {
  id: string;
  node_id: string;
  label: string;
  definition?: string | null;
  properties?: unknown;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (dto: {
    name: string;
    description?: string;
    knowledge_base_id: string;
    ontology_id?: string;
    anchor_node_id?: string;
    schema: DatasetColumn[];
  }) => void;
  knowledgeBases: KnowledgeBase[];
  loading?: boolean;
}

const COLUMN_DTYPES: DatasetColumn['dtype'][] = ['number', 'text', 'boolean'];
const COLUMN_ROLES: { value: DatasetColumn['role']; label: string }[] = [
  { value: 'identifier', label: 'Identifier (ID)' },
  { value: 'feature',    label: 'Feature (input)' },
  { value: 'target',     label: 'Target (output)' },
];

const EMPTY_COL = (): DatasetColumn => ({ key: '', label: '', role: 'feature', dtype: 'number', unit: '' });

export function CreateDatasetModal({ open, onClose, onCreated, knowledgeBases, loading }: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [kbId, setKbId] = useState('');
  const [columns, setColumns] = useState<DatasetColumn[]>([EMPTY_COL()]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ── Suggest-schema-from-document state ─────────────────────────────────────
  const [documents, setDocuments] = useState<KBDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [selectedKoId, setSelectedKoId] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);

  // ── Suggest-schema-from-ontology state ─────────────────────────────────────
  const [ontologies, setOntologies] = useState<Ontology[]>([]);
  const [ontosLoading, setOntosLoading] = useState(false);
  const [selectedOntologyId, setSelectedOntologyId] = useState('');
  const [ontoNodes, setOntoNodes] = useState<RawOntologyNode[]>([]);
  const [nodesLoading, setNodesLoading] = useState(false);
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [suggestingOnto, setSuggestingOnto] = useState(false);
  const [suggestOntoError, setSuggestOntoError] = useState<string | null>(null);
  // The ontology + nodes chosen when schema was last suggested from ontology
  const [schemaOntologyId, setSchemaOntologyId] = useState<string | undefined>(undefined);
  const [schemaNodeId, setSchemaNodeId] = useState<string | undefined>(undefined);

  // Load documents when KB changes
  useEffect(() => {
    if (!kbId) {
      setDocuments([]);
      setSelectedKoId('');
      return;
    }
    setDocsLoading(true);
    setSelectedKoId('');
    knowledgeBaseApi
      .listDocuments(kbId)
      .then((docs) => setDocuments(docs))
      .catch(() => setDocuments([]))
      .finally(() => setDocsLoading(false));
  }, [kbId]);

  // Load ontologies once (when modal opens)
  useEffect(() => {
    if (!open) return;
    setOntosLoading(true);
    ontologyApi
      .getOntologies()
      .then((list) => setOntologies(list))
      .catch(() => setOntologies([]))
      .finally(() => setOntosLoading(false));
  }, [open]);

  // Load nodes when ontology changes (via the full ontology response which includes nodes)
  useEffect(() => {
    if (!selectedOntologyId) {
      setOntoNodes([]);
      setSelectedNodeIds([]);
      return;
    }
    setNodesLoading(true);
    setSelectedNodeIds([]);
    ontologyApi
      .getOntology(selectedOntologyId)
      .then((onto) => setOntoNodes((onto as any).nodes ?? []))
      .catch(() => setOntoNodes([]))
      .finally(() => setNodesLoading(false));
  }, [selectedOntologyId]);

  function reset() {
    setName('');
    setDescription('');
    setKbId('');
    setColumns([EMPTY_COL()]);
    setErrors({});
    setDocuments([]);
    setSelectedKoId('');
    setSuggesting(false);
    setSuggestError(null);
    setSelectedOntologyId('');
    setOntoNodes([]);
    setSelectedNodeIds([]);
    setSuggestingOnto(false);
    setSuggestOntoError(null);
    setSchemaOntologyId(undefined);
    setSchemaNodeId(undefined);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function addColumn() {
    setColumns(cs => [...cs, EMPTY_COL()]);
  }

  function removeColumn(idx: number) {
    setColumns(cs => cs.filter((_, i) => i !== idx));
  }

  function updateColumn(idx: number, patch: Partial<DatasetColumn>) {
    setColumns(cs => cs.map((c, i) => i === idx ? { ...c, ...patch } : c));
  }

  async function handleSuggest() {
    if (!selectedKoId) return;
    setSuggesting(true);
    setSuggestError(null);
    // Clear ontology provenance since document suggestion wins
    setSchemaOntologyId(undefined);
    setSchemaNodeId(undefined);
    try {
      const suggested = await datasetApi.suggestSchema(selectedKoId);
      if (suggested.length === 0) {
        setSuggestError('No tables found in this document. Try a different one.');
      } else {
        setColumns(suggested.map(c => ({ ...c, unit: c.unit ?? '' })));
      }
    } catch {
      setSuggestError('Schema suggestion failed. You can still define columns manually.');
    } finally {
      setSuggesting(false);
    }
  }

  async function handleSuggestFromOntology() {
    if (!selectedOntologyId || selectedNodeIds.length === 0) return;
    setSuggestingOnto(true);
    setSuggestOntoError(null);
    // Clear document suggestion error
    setSuggestError(null);
    try {
      const suggested = await datasetApi.suggestSchemaFromOntology(selectedOntologyId, selectedNodeIds, {
        datasetName: name.trim() || undefined,
        datasetDescription: description.trim() || undefined,
      });
      if (suggested.length === 0) {
        setSuggestOntoError('No measurable properties found for the selected concepts. Try different ones.');
      } else {
        setColumns(suggested.map(c => ({ ...c, unit: c.unit ?? '' })));
        // Remember provenance (first selected node as anchor)
        setSchemaOntologyId(selectedOntologyId);
        setSchemaNodeId(selectedNodeIds[0]);
      }
    } catch {
      setSuggestOntoError('Schema suggestion failed. You can still define columns manually.');
    } finally {
      setSuggestingOnto(false);
    }
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Name is required';
    if (!kbId)        errs.kbId = 'Knowledge base is required';
    columns.forEach((c, i) => {
      if (!c.key.trim())   errs[`col_key_${i}`]   = 'Column key required';
      if (!c.label.trim()) errs[`col_label_${i}`] = 'Column label required';
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onCreated({
      name: name.trim(),
      description: description.trim() || undefined,
      knowledge_base_id: kbId,
      ontology_id: schemaOntologyId,
      anchor_node_id: schemaNodeId,
      schema: columns.map(c => ({
        ...c,
        key:   c.key.trim().replace(/\s+/g, '_').toLowerCase(),
        label: c.label.trim(),
        unit:  c.unit?.trim() || undefined,
      })),
    });
    reset();
  }

  // Documents that are fully converted (have chunked content)
  const readyDocs = documents.filter(d => d.conversion_status === 'completed');

  // Box style shared between the two suggest panels
  const boxStyle: React.CSSProperties = {
    background: 'var(--cds-layer-01)',
    border: '1px solid var(--cds-border-subtle-01)',
    borderRadius: '4px',
    padding: '1rem',
    flex: '1 1 0',
    minWidth: 0,
  };

  return (
    <Modal
      open={open}
      modalHeading="New ML Dataset"
      primaryButtonText={loading ? undefined : 'Create dataset'}
      primaryButtonDisabled={loading}
      secondaryButtonText="Cancel"
      onRequestClose={handleClose}
      onRequestSubmit={handleSubmit}
      onSecondarySubmit={handleClose}
      size="lg"
    >
      {loading && <InlineLoading description="Creating…" style={{ marginBottom: '1rem' }} />}

      <Stack gap={5}>
        {/* Name + description */}
        <TextInput
          id="ds-name"
          labelText="Dataset name"
          placeholder="e.g. UV Sol-Gel Adhesion"
          value={name}
          onChange={e => setName(e.target.value)}
          invalid={!!errors.name}
          invalidText={errors.name}
        />
        <TextArea
          id="ds-desc"
          labelText="Description (optional)"
          placeholder="What formulation properties will this dataset capture?"
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={2}
        />

        {/* Knowledge base selector */}
        <Select
          id="ds-kb"
          labelText="Knowledge base"
          value={kbId}
          onChange={e => setKbId(e.target.value)}
          invalid={!!errors.kbId}
          invalidText={errors.kbId}
        >
          <SelectItem value="" text="— choose a knowledge base —" />
          {knowledgeBases.map(kb => (
            <SelectItem key={kb.id} value={kb.id} text={kb.name} />
          ))}
        </Select>

        {/* Suggest schema — 2-column layout (document | ontology) */}
        {kbId && (
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'stretch' }}>
            {/* ── Left: from document ─────────────────────────────────────── */}
            <div style={boxStyle}>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                Suggest schema from document
              </p>
              <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginBottom: '0.75rem' }}>
                Pick a document from this knowledge base and let the AI propose a column schema based on the tables it contains.
              </p>

              {docsLoading ? (
                <InlineLoading description="Loading documents…" />
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <Select
                      id="ds-suggest-doc"
                      labelText="Document"
                      value={selectedKoId}
                      onChange={e => { setSelectedKoId(e.target.value); setSuggestError(null); }}
                      disabled={readyDocs.length === 0}
                    >
                      <SelectItem
                        value=""
                        text={readyDocs.length === 0 ? '— no processed documents —' : '— choose a document —'}
                      />
                      {readyDocs.map(doc => (
                        <SelectItem key={doc.id} value={doc.id} text={doc.filename} />
                      ))}
                    </Select>
                  </div>
                  <Button
                    kind="tertiary"
                    size="md"
                    renderIcon={suggesting ? undefined : MagicWand}
                    disabled={!selectedKoId || suggesting}
                    onClick={handleSuggest}
                    style={{ minWidth: '10rem', whiteSpace: 'nowrap' }}
                  >
                    {suggesting ? <InlineLoading description="Suggesting…" /> : 'Suggest schema'}
                  </Button>
                </div>
              )}

              {suggestError && (
                <InlineNotification
                  kind="warning"
                  title=""
                  subtitle={suggestError}
                  lowContrast
                  hideCloseButton
                  style={{ marginTop: '0.5rem' }}
                />
              )}
            </div>

            {/* ── Right: from ontology ─────────────────────────────────────── */}
            <div style={boxStyle}>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                Use an ontology to create the schema
              </p>
              <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginBottom: '0.75rem' }}>
                Select an ontology and a concept to let the AI derive measurable properties as schema columns.
              </p>

              {ontosLoading ? (
                <InlineLoading description="Loading ontologies…" />
              ) : (
                <Stack gap={3}>
                  <Select
                    id="ds-onto-select"
                    labelText="Ontology"
                    value={selectedOntologyId}
                    onChange={e => {
                      setSelectedOntologyId(e.target.value);
                      setSuggestOntoError(null);
                    }}
                    disabled={ontologies.length === 0}
                  >
                    <SelectItem
                      value=""
                      text={ontologies.length === 0 ? '— no ontologies available —' : '— choose an ontology —'}
                    />
                    {ontologies.map(o => (
                      <SelectItem key={o.id} value={o.id} text={o.name} />
                    ))}
                  </Select>

                  {selectedOntologyId && (
                    nodesLoading ? (
                      <InlineLoading description="Loading concepts…" />
                    ) : (
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                          <MultiSelect
                            id="ds-onto-node-select"
                            titleText="Concepts"
                            label={ontoNodes.length === 0 ? '— no concepts found —' : '— choose concepts —'}
                            items={ontoNodes.map(n => ({ id: n.node_id, label: n.label }))}
                            itemToString={(item: { id: string; label: string } | null) => item?.label ?? ''}
                            selectedItems={ontoNodes
                              .filter(n => selectedNodeIds.includes(n.node_id))
                              .map(n => ({ id: n.node_id, label: n.label }))}
                            onChange={({ selectedItems }: { selectedItems: Array<{ id: string; label: string }> | null }) => {
                              setSelectedNodeIds((selectedItems ?? []).map(i => i.id));
                              setSuggestOntoError(null);
                            }}
                            disabled={ontoNodes.length === 0}
                          />
                        </div>
                        <Button
                          kind="tertiary"
                          size="md"
                          renderIcon={suggestingOnto ? undefined : MagicWand}
                          disabled={selectedNodeIds.length === 0 || suggestingOnto}
                          onClick={handleSuggestFromOntology}
                          style={{ minWidth: '10rem', whiteSpace: 'nowrap' }}
                        >
                          {suggestingOnto ? <InlineLoading description="Suggesting…" /> : 'Suggest schema'}
                        </Button>
                      </div>
                    )
                  )}
                </Stack>
              )}

              {suggestOntoError && (
                <InlineNotification
                  kind="warning"
                  title=""
                  subtitle={suggestOntoError}
                  lowContrast
                  hideCloseButton
                  style={{ marginTop: '0.5rem' }}
                />
              )}
            </div>
          </div>
        )}

        {/* Schema editor */}
        <div>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
            Column schema
            <Tag type="gray" style={{ marginLeft: '0.5rem' }}>{columns.length} column{columns.length !== 1 ? 's' : ''}</Tag>
          </div>
          <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginBottom: '0.75rem' }}>
            Define the properties the AI agent should extract from each paper sample row.
          </p>

          {columns.map((col, idx) => {
            // At most one identifier column allowed; disable the option on
            // other rows when one is already assigned.
            const identifierTaken = columns.some((c, i) => i !== idx && c.role === 'identifier');
            return (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 120px 100px 80px auto',
                  gap: '0.5rem',
                  marginBottom: '0.5rem',
                  alignItems: 'flex-end',
                }}
              >
                <TextInput
                  id={`col-key-${idx}`}
                  labelText={idx === 0 ? 'Key (snake_case)' : ''}
                  placeholder="adhesion_score"
                  value={col.key}
                  onChange={e => updateColumn(idx, { key: e.target.value })}
                  invalid={!!errors[`col_key_${idx}`]}
                  invalidText={errors[`col_key_${idx}`]}
                  size="sm"
                />
                <TextInput
                  id={`col-label-${idx}`}
                  labelText={idx === 0 ? 'Display label' : ''}
                  placeholder="Adhesion Score"
                  value={col.label}
                  onChange={e => updateColumn(idx, { label: e.target.value })}
                  invalid={!!errors[`col_label_${idx}`]}
                  invalidText={errors[`col_label_${idx}`]}
                  size="sm"
                />
                <Select
                  id={`col-role-${idx}`}
                  labelText={idx === 0 ? 'Role' : ''}
                  value={col.role}
                  onChange={e => updateColumn(idx, { role: e.target.value as DatasetColumn['role'] })}
                  size="sm"
                >
                  {COLUMN_ROLES.map(r => (
                    <SelectItem
                      key={r.value}
                      value={r.value}
                      text={r.label}
                      disabled={r.value === 'identifier' && identifierTaken}
                    />
                  ))}
                </Select>
                <Select
                  id={`col-dtype-${idx}`}
                  labelText={idx === 0 ? 'Type' : ''}
                  value={col.dtype}
                  onChange={e => updateColumn(idx, { dtype: e.target.value as DatasetColumn['dtype'] })}
                  size="sm"
                >
                  {COLUMN_DTYPES.map(t => (
                    <SelectItem key={t} value={t} text={t} />
                  ))}
                </Select>
                <TextInput
                  id={`col-unit-${idx}`}
                  labelText={idx === 0 ? 'Unit' : ''}
                  placeholder="MPa"
                  value={col.unit ?? ''}
                  onChange={e => updateColumn(idx, { unit: e.target.value })}
                  size="sm"
                />
                <Button
                  kind="ghost"
                  size="sm"
                  iconDescription="Remove column"
                  renderIcon={TrashCan}
                  hasIconOnly
                  onClick={() => removeColumn(idx)}
                  disabled={columns.length === 1}
                  style={{
                    color: 'var(--cds-support-error, #da1e28)',
                    marginBottom: errors[`col_key_${idx}`] || errors[`col_label_${idx}`] ? '1.25rem' : 0,
                  }}
                />
              </div>
            );
          })}

          <Button
            kind="ghost"
            size="sm"
            renderIcon={Add}
            onClick={addColumn}
            style={{ marginTop: '0.25rem' }}
          >
            Add column
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}

// Made with Bob
